import { later } from "@ember/runloop"
import Service, { inject as service } from "@ember/service"
import type { ChangeGroupStateSignature } from "cc-frontend/components/admin/fiscal-group/change-group-state"
import type { AddMembersInterface } from "cc-frontend/components/group/add-members"
import type { CreateTeamSignature } from "cc-frontend/components/group/create-team"
import type { SelectTeamsSignature } from "cc-frontend/components/group/select-teams"
import type { ShareUnsharePlanbooksSignature } from "cc-frontend/components/group/share-unshare-planbooks"
import type { CourseCreateModalSignature } from "cc-frontend/components/planner/course/create-edit/create-modal"
import type { CourseEditModalSignature } from "cc-frontend/components/planner/course/create-edit/edit-modal"
import type { TimelineSidebarSignature } from "cc-frontend/components/planner/course/timeline/sidebar"
import type { EmailLinkFormSignature } from "cc-frontend/components/planner/menus/parts/email-link-form"
import type { Args as EditAnnotationBodyArgs } from "cc-frontend/components/planner/modals/edit-annotation"
import type { EditCourseModalComponentSignature } from "cc-frontend/components/planner/modals/edit-course"
import type { FollowCourseModalSignature } from "cc-frontend/components/planner/modals/follow-course"
import type { FullScreenModalArgs } from "cc-frontend/components/planner/modals/full-screen"
import type { GoogleClassroomModalComponentSignature } from "cc-frontend/components/planner/modals/google-classroom"
import type { MigratingToUnifiedLessonsModalArgs } from "cc-frontend/components/planner/modals/migrating-to-unified-lessons"
import type { TemplateLibraryModalArgs } from "cc-frontend/components/planner/modals/template-library"
import type { UnsplashModalSignature } from "cc-frontend/components/planner/modals/unsplash-modal"
import type { ListModalSignature } from "cc-frontend/components/planner/planbook/special-events/list-modal"
import type { SchoolEditNameSignature } from "cc-frontend/components/school/edit/name"
import type { Signature } from "cc-frontend/components/school/setup"
import type { UnlockGroupFormAdminSignature } from "cc-frontend/components/user/unlock-group-form/admin"
import type { UnlockGroupFormDistrictAdminSignature } from "cc-frontend/components/user/unlock-group-form/district-admin"
import type { UnlockGroupFormTeacherSignature } from "cc-frontend/components/user/unlock-group-form/teacher"
import openModalAction from "cc-frontend/lib/actions/app-state/OPEN_MODAL"
import { concat, filter, find, forEach, includes, isNil, last, pull, sortBy } from "lodash-es"
import { tracked } from "tracked-built-ins"
import type Dispatcher from "./dispatcher"

// Scott got this from Stackoverflow but forgot where.  Any bugs are the original posters
// fault. All the success is from Scott.
const tuple = <T extends string[]>(...args: T) => args

const slideoutModalNames = tuple(
  "group/create-team",
  "group/add-members",
  "group/select-teams",
  "group/share-unshare-planbooks",
  "school/edit/name",
  "user/account",
  "user/dub-referrals",
  "user/create-planbook",
  "admin/fiscal-group/change-group-state",
  "planner/course/timeline/sidebar",
  "planner/modals/google-classroom",
  "planner/modals/google-sign-in",
  "planner/modals/print-paywall",
  "planner/modals/print-download",
  "planner/modals/edit-annotation",
  "planner/planbook/copy-planbook/modal",
  "planner/planbook/special-events/list-modal",
)

const fullScreenModalNames = tuple(
  "planner/course/create-edit/create-modal",
  "planner/course/create-edit/edit-modal",
  "planner/menus/parts/email-link-form",
  "planner/modals/edit-course",
  "planner/modals/full-screen",
  "planner/modals/lesson-history",
  "planner/modals/follow-course",
  "planner/modals/migrating-to-unified-lessons",
  "planner/modals/template-library",
  "planner/planbook/create-edit/edit-modal",
  "planner/modals/unsplash-modal",
  "planner/modals/import-lessons",
  "planner/modals/unit-comparison-modal",
  "planner/modals/weekly-order",
  "school/setup",
  "user/unlock-group-form/admin",
  "user/unlock-group-form/district-admin",
  "user/unlock-group-form/teacher",
)

const scrollableModals = tuple("planner/course/timeline/sidebar")

/**
 * This is what you pass into the modal.
 */
interface ModelTypes {
  "admin/fiscal-group/change-group-state": ChangeGroupStateSignature["Args"]["model"]
  "group/add-members": AddMembersInterface["Args"]["model"]
  "group/create-team": CreateTeamSignature["Args"]["model"]
  "group/select-teams": SelectTeamsSignature["Args"]["model"]
  "group/share-unshare-planbooks": ShareUnsharePlanbooksSignature["Args"]["model"]
  "planner/course/create-edit/create-modal": CourseCreateModalSignature["Args"]["model"]
  "planner/course/create-edit/edit-modal": CourseEditModalSignature["Args"]["model"]
  "planner/course/timeline/sidebar": TimelineSidebarSignature["Args"]["model"]
  "planner/menus/parts/email-link-form": EmailLinkFormSignature["Args"]["model"]
  "planner/modals/edit-annotation": EditAnnotationBodyArgs
  "planner/modals/edit-course": EditCourseModalComponentSignature["Args"]["model"]
  "planner/modals/full-screen": FullScreenModalArgs
  "planner/modals/google-classroom": GoogleClassroomModalComponentSignature["Args"]["model"]
  "planner/modals/google-sign-in": any
  "planner/modals/import-lessons": any
  "planner/modals/lesson-history": any
  "planner/modals/follow-course": FollowCourseModalSignature["Args"]["model"]
  "planner/modals/migrating-to-unified-lessons": MigratingToUnifiedLessonsModalArgs
  "planner/modals/print-download": any
  "planner/modals/print-paywall": any
  "planner/modals/template-library": TemplateLibraryModalArgs
  "planner/modals/unit-comparison-modal": any
  "planner/modals/weekly-order": any
  "planner/planbook/copy-planbook/modal": any
  "planner/planbook/create-edit/edit-modal": any
  "planner/planbook/special-events/list-modal": ListModalSignature["Args"]["model"]
  "planner/modals/unsplash-modal": UnsplashModalSignature["Args"]["model"]
  "school/edit/name": SchoolEditNameSignature["Args"]["model"]
  "user/account": any
  "user/dub-referrals": any
  "user/create-planbook": any
  "user/unlock-group-form/admin": UnlockGroupFormAdminSignature["Args"]["model"]
  "user/unlock-group-form/district-admin": UnlockGroupFormDistrictAdminSignature["Args"]["model"]
  "user/unlock-group-form/teacher": UnlockGroupFormTeacherSignature["Args"]["model"]
  "school/setup": Signature["Args"]["model"]
}

const modalNames = concat(slideoutModalNames, fullScreenModalNames)
export type ModalName = (typeof modalNames)[number]

class ModalProps {
  @tracked isVisible: boolean = false
  @tracked model: any | null = null
  @tracked order: number = 0
  @tracked modalName: string
  @tracked modalType: "FULL_SCREEN" | "SLIDEOUT"
  @tracked onClose: undefined | ((promise: Promise<void>) => void) = undefined

  constructor(modalName: string, modalType: "FULL_SCREEN" | "SLIDEOUT") {
    this.modalName = modalName
    this.modalType = modalType
  }
}

export default class ModalManager extends Service {
  @service declare dispatcher: Dispatcher

  // TODO this should be converted into a getter which just counts which modals have `isVisible`
  // set to true and then adds in the modalsOPenedOutsisdeManager to it.
  @tracked openedModalsCount = 0

  // @ts-ignore This should really be fine.
  openedModals: Array<ModalName> = tracked<ModalName>([])
  modals!: Array<ModalProps>

  constructor(...args) {
    super(...args)

    this.modals = modalNames.map((name) => {
      const modalType = includes(fullScreenModalNames, name) ? "FULL_SCREEN" : "SLIDEOUT"
      return new ModalProps(name, modalType)
    })
  }

  openModal<ModalName extends keyof ModelTypes, model extends ModelTypes[ModalName]>(
    componentName: ModalName,
    model: model,
    opts?: { onClose: (Promise) => void },
  ) {
    let modal = find(this.modals, (modalProp) => modalProp.modalName === componentName)
    if (isNil(modal))
      throw Error(
        `${componentName} has not been added to the modal manager. Go to services/modal-manager.ts to add it.`,
      )

    if (modal.isVisible === false) {
      this.openedModalsCount++
      modal.isVisible = true
    }
    this.openedModals.push(componentName)
    modal.model = model
    modal.order = this.openedModalsCount
    modal.onClose = opts?.onClose
    let appBody = document.querySelector("body")
    appBody?.classList.add("modal-manager-open")

    if (includes(scrollableModals, modal.modalName)) {
      appBody?.classList.add("--is-scrollable")
    } else {
      appBody?.classList.remove("--is-scrollable")
    }

    this.dispatcher.dispatchPreparedAction(openModalAction({ modalName: componentName }))
  }

  closeModal(componentName: ModalName) {
    let modal = find(this.modals, (modalProp) => modalProp.modalName === componentName)
    if (isNil(modal))
      throw Error(
        `${componentName} has not been added to the modal manager. Go to services/modal-manager.ts to add it.`,
      )

    if (modal.isVisible === true) {
      modal.isVisible = false
      this.openedModalsCount = this.openedModalsCount - 1

      let closePromise = new Promise<void>((resolve) => {
        // Scott's pretty sure this is delayed to let the animation complete
        later(() => {
          if (modal) {
            modal.model = null
            modal.order = 0
            resolve()
          }
        }, 500)
      })
      if (modal.onClose) modal.onClose(closePromise)

      let appBody = document.querySelector("body")
      if (this.openedModalsCount === 0) {
        appBody?.classList.remove("modal-manager-open")
      }

      pull(this.openedModals, componentName)
      let lastModalOpened = last(sortBy(filter(this.modals, "isVisible"), "order"))

      if (!isNil(lastModalOpened) && includes(scrollableModals, lastModalOpened.modalName)) {
        appBody?.classList.add("--is-scrollable")
      } else {
        appBody?.classList.remove("--is-scrollable")
      }
    }
  }

  closeAllModals() {
    forEach(this.modals, (modalProp) => {
      if (modalProp.isVisible === true) {
        modalProp.isVisible = false
        --this.openedModalsCount
      }
    })
    // @ts-ignore
    this.openedModals = tracked<string>([])
    document.querySelector("body")?.classList.remove("modal-manager-open")
  }

  openModalOutsideManager() {
    this.openedModalsCount = this.openedModalsCount + 1
    document.querySelector("body")?.classList.add("modal-manager-open")
  }

  closeModalOutsideManager() {
    this.openedModalsCount = this.openedModalsCount - 1
    if (this.openedModalsCount === 0) {
      document.querySelector("body")?.classList.remove("modal-manager-open")
    }
  }
}
